import { connect } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router';
import { isHostedOnProductionURL, isLocalHost } from 'myParkdUtils/utils';
import ModalWindows from 'components/ModalWindows';
import OfflineNotification from 'components/OfflineNotification';
import PageViewTracker from 'components/board/PageViewTracker';
import GlobalErrorBoundary from 'components/GlobalErrorBoundary';
import { ReduxState } from 'types';
import { useEffect, useRef } from 'react';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import ProtectedRoutes from 'components/ProtectedRoutes';
import PublicRoutes from 'components/PublicRoutes';
import * as appActions from 'root/appActions';
import * as Sentry from "@sentry/react";
// Screens
import LoginScreen from 'screens/LoginScreen';
import ResetPasswordScreen from 'screens/ResetPasswordScreen';
import PageNotFoundScreen from 'screens/PageNotFoundScreen';
import SettingsScreen from 'screens/SettingsScreen';
import ParkingSessionsScreen from 'screens/parkingSessions/ParkingSessionsScreen';
import VehiclesScreen from 'screens/VehiclesScreen';
import SupportScreen from 'screens/SupportScreen';
import ImportLicensePlatesScreen from 'screens/vehicles-import/ImportLicensePlatesScreen';
import OnboardingScreen from 'screens/registration/OnboardingScreen';
import RegistrationScreen from 'screens/registration/RegistrationScreen';
import RegistrationCompletedScreen from 'screens/registration/RegistrationCompletedScreen';
import MandateSignedScreen from 'screens/MandateSignedScreen';
import UsersScreen from 'screens/UsersScreen';
import ParkdCardsScreen from 'screens/parkdCards/ParkdCardsScreen';
import CookieConsentSetup from 'components/cookieConsentSetup';


const App = (props: Props) => {
    const firstMount = useRef<boolean>(true);
    // New adviced way to execute function only once (works in React18 + Strictmode)
    useEffect(() => {
        if (firstMount.current) {
            firstMount.current = false;
            props.initialiseMyParkd();
        }
    }, []);

    if (isLocalHost()) {
        // To easily discover rerender bugs in App.tsx
        console.warn('rerender of whole App component!', props);
    }

    if (!props.storageLoaded) {
        return <div />;
    }

    const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

    return (
        <BrowserRouter>
            {isHostedOnProductionURL() && (
                <GlobalErrorBoundary className="hidden">
                    <PageViewTracker />
                </GlobalErrorBoundary>
            )}

            <CookieConsentSetup />

            <ModalWindows />

            <OfflineNotification />

            <SentryRoutes>
                <Route element={<ProtectedRoutes />}>
                    <Route path="/" element={<VehiclesScreen />} />
                    <Route path="/vehicles" element={<VehiclesScreen />} />
                    <Route path="/users" element={<UsersScreen />} />
                    <Route path='/sessions' element={<ParkingSessionsScreen />} />
                    <Route path='/support' element={<SupportScreen />} />
                    <Route path='/settings' element={<SettingsScreen />} />
                    <Route path='/vehicles/import-license-plates' element={<ImportLicensePlatesScreen />} />
                    <Route path='/parkd-cards' element={<ParkdCardsScreen />} />
                    <Route path='/onboarding' element={<OnboardingScreen />} />
                </Route>

                <Route element={<PublicRoutes />}>
                    <Route path="/mandate-signed/" element={<MandateSignedScreen />} />
                    <Route path="/registration-completed/" element={<RegistrationCompletedScreen />} />
                    <Route path="/login" element={<LoginScreen />} />
                    <Route path="/reset-password" element={<ResetPasswordScreen />} />
                    <Route path='/register' element={<RegistrationScreen />} />
                    <Route path='/register-trackandtrace' element={<RegistrationScreen />} />
                    <Route path='/register-oem' element={<RegistrationScreen />} />
                </Route>

                <Route path="*" element={<PageNotFoundScreen />} />
            </SentryRoutes>

        </BrowserRouter >
    );
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

function mapStateToProps(state: ReduxState) {
    return {
        storageLoaded: state.storage?.storageLoaded,
        // CAUTION! Loading props here can result in unwanted rerenders of the whole App!
        // that will rerender all subcomponents and make them loose their state!
    };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>) {
    return bindActionCreators({
        initialiseMyParkd: appActions.initialiseMyParkd,
    }, dispatch)
}

export default connect(mapStateToProps, mapDispatchToProps)(App);